/**
 * LiveCard — side card "live" générique du profil Particulier.
 *
 * Card panel Linear/Stripe-style avec :
 *  - Header avec live-dot + eyebrow mono
 *  - Slot principal (children)
 *  - Footer optionnel (footer note, séparateur dégradé)
 *  - Halo gradient flouté derrière (auto)
 *  - Animation framer entrée (scale + y)
 *
 * Utilisée comme side panel du hero (PageHero) ou dans le CTA dark immersif.
 */

import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';

export interface ParticulierLiveCardProps {
  /** Eyebrow titre */
  eyebrow?: ReactNode;
  /** Sous-titre du header (sous l'eyebrow) */
  subEyebrow?: ReactNode;
  /** Badge à droite du header (chip "2026", "Exemple"…) */
  badge?: ReactNode;
  /** Corps de la card */
  children: ReactNode;
  /** Footer texte (sépare avec un divider) */
  footer?: ReactNode;
  /** Variant visuel : light (default) ou dark (pour CTA dark) */
  variant?: 'light' | 'dark';
  /** Halo gradient derrière la card (par défaut : oui) */
  withHalo?: boolean;
  /** Classes additionnelles */
  className?: string;
  /** Délai d'entrée framer (en seconds) */
  motionDelay?: number;
}

export function ParticulierLiveCard({
  eyebrow,
  subEyebrow,
  badge,
  children,
  footer,
  variant = 'light',
  withHalo = true,
  className,
  motionDelay = 0.18,
}: ParticulierLiveCardProps) {
  const isDark = variant === 'dark';

  const cardClass = isDark
    ? 'rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md ring-1 ring-inset ring-white/5 md:p-6'
    : 'live-card p-5 md:p-6';

  const labelClass = isDark
    ? 'text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory-50/60'
    : 'text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500';

  const subClass = isDark ? 'mt-0.5 text-xs text-ivory-50/70' : 'mt-0.5 text-xs text-stone-500';
  const dividerClass = isDark ? 'border-white/10' : 'border-stone-200/60';
  const footerClass = isDark ? 'text-[11px] text-ivory-50/60' : 'text-[11px] text-stone-500';

  return (
    <div className={cn('relative', className)}>
      {withHalo && !isDark && (
        <div
          aria-hidden
          className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-brand-100/40 via-accent-100/30 to-transparent blur-3xl"
        />
      )}
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 16 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.55, delay: motionDelay, ease: easeStripe }}
        className={cardClass}
      >
        {(eyebrow || badge) && (
          <div className="flex items-start justify-between">
            <div>
              {eyebrow && <p className={labelClass}>{eyebrow}</p>}
              {subEyebrow && <p className={subClass}>{subEyebrow}</p>}
            </div>
            {badge}
          </div>
        )}

        <div className={eyebrow || badge ? 'mt-5' : ''}>{children}</div>

        {footer && (
          <div className={cn('mt-5 border-t pt-3', dividerClass)}>
            <div className={cn('font-mono', footerClass)}>{footer}</div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
